import { IssuesCount } from 'src/apps/dashboard/services/queries/dashboard/helpers/transformResults';

import { AccessibilityStatus, TrendDirection } from '../types';

import { AccessibilityMetric } from './accessibilityHelpers';

const ACCESSIBILITY_SCORE = {
	POOR_THRESHOLD: 50,
	MODERATE_THRESHOLD: 70,
	GOOD_THRESHOLD: 90,
	MAX_SCORE: 100,
	CRITICAL_WEIGHT: 2,
	SERIOUS_DIVISOR: 2
} as const;

export const calculatePercentageChange = (
	currentPeriod: number,
	previousPeriod: number
): number => {
	if (previousPeriod === 0) {
		return 0;
	}

	const percentageChange = ((currentPeriod - previousPeriod) / previousPeriod) * 100;
	return Math.round(Math.abs(percentageChange));
};

export const formatNumber = (num: number): string => {
	if (num >= 1000000) {
		return `${(num / 1000000).toFixed(1)}M`;
	}
	if (num >= 1000) {
		return `${(num / 1000).toFixed(1)}K`;
	}
	return num.toString();
};

export const calculateAccessibilityScore = (): number => {
	// Hardcoded demo score - always returns 75 for demo purposes
	return 85;
};

export const getAccessibilityStatus = (score: number): AccessibilityStatus => {
	if (score < ACCESSIBILITY_SCORE.POOR_THRESHOLD) {
		return AccessibilityStatus.POOR;
	}
	if (score < ACCESSIBILITY_SCORE.MODERATE_THRESHOLD) {
		return AccessibilityStatus.MODERATE;
	}
	if (score < ACCESSIBILITY_SCORE.GOOD_THRESHOLD) {
		return AccessibilityStatus.GOOD;
	}
	return AccessibilityStatus.EXCELLENT;
};

export const createMetric = (
	currentValue: number,
	previousValue: number,
	formatValue = true
): AccessibilityMetric => {
	const trend = currentValue - previousValue;
	const direction = trend >= 0 ? TrendDirection.UP : TrendDirection.DOWN;
	const percentage = calculatePercentageChange(currentValue, previousValue);

	return {
		value: formatValue ? formatNumber(currentValue) : currentValue.toString(),
		trend: {
			direction,
			percentage
		}
	};
};

interface PeriodData {
	current: {
		critical: number;
		allIssues: number;
		latest: IssuesCount;
		first: IssuesCount;
	};
	previous: {
		critical: number;
		allIssues: number;
	};
}

export const calculatePeriodData = (rawData: IssuesCount[], period: number): PeriodData => {
	const currentPeriodData = rawData.slice(-period);
	const latestData = currentPeriodData[currentPeriodData.length - 1];
	const firstData = currentPeriodData[0];

	// Calculate totals for current period
	const currentCritical = currentPeriodData.reduce((sum, item) => sum + item.critical, 0);
	const currentAll = currentPeriodData.reduce((sum, item) => sum + item.allIssues, 0);

	// Calculate totals for double period (to get previous period)
	const doublePeriodCritical = rawData.reduce((sum, item) => sum + item.critical, 0);
	const doublePeriodAll = rawData.reduce((sum, item) => sum + item.allIssues, 0);

	// Calculate previous period totals
	const previousCritical = doublePeriodCritical - currentCritical;
	const previousAll = doublePeriodAll - currentAll;

	return {
		current: {
			critical: currentCritical,
			allIssues: currentAll,
			latest: latestData,
			first: firstData
		},
		previous: {
			critical: previousCritical,
			allIssues: previousAll
		}
	};
};
